import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Star, 
  Users, 
  Award, 
  FileText, 
  Smartphone,
  Zap,
  Shield,
  Download,
  Eye
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const LandingPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <FileText className="w-8 h-8 text-primary-600" />,
      title: "Professional Templates",
      description: "Choose from 25+ ATS-friendly templates designed by experts"
    },
    {
      icon: <Zap className="w-8 h-8 text-primary-600" />,
      title: "AI Template Recommendations",
      description: "Let AI analyze your profile and recommend the perfect template"
    },
    {
      icon: <Smartphone className="w-8 h-8 text-primary-600" />,
      title: "Mobile Friendly",
      description: "Create and edit resumes on any device, anywhere"
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "ATS Optimized",
      description: "Templates designed to pass Applicant Tracking Systems"
    },
    {
      icon: <Download className="w-8 h-8 text-primary-600" />,
      title: "Multiple Formats",
      description: "Export as PDF, Word, or plain text"
    },
    {
      icon: <Eye className="w-8 h-8 text-primary-600" />,
      title: "Real-time Preview",
      description: "See changes as you type with live preview"
    }
  ];

  const stats = [
    { number: "38%", labelKey: "landing.statsMoreInterviews" },
    { number: "23%", labelKey: "landing.statsHigherOffers" },
    { number: "500+", labelKey: "landing.statsResumeExamples" },
    { number: "25+", labelKey: "landing.statsProfessionalTemplates" }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Software Engineer",
      company: "Tech Corp",
      text: "The AI suggestions helped me create a much stronger resume. I got 3 interviews in the first week!",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Marketing Manager",
      company: "Global Marketing",
      text: "Professional templates and easy customization. My resume looks amazing and I landed my dream job.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Nurse Practitioner",
      company: "City Hospital",
      text: "Perfect for healthcare professionals. The templates are clean and ATS-friendly.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-primary-100 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              <span className="text-gradient">
                {t('landing.heroTitle')}
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
            >
              {t('landing.heroSubtitle')}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/register" className="btn-primary text-lg px-8 py-4">
                {t('landing.ctaCreateResume')}
              </Link>
              <Link to="/ai-recommendations" className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg px-8 py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 inline-flex items-center justify-center shadow-lg">
                <Zap className="w-5 h-5 mr-2" />
                {t('landing.ctaAIRecommendations')}
              </Link>
              <Link 
                to="/resume-analyzer" 
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg px-8 py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 inline-flex items-center justify-center shadow-lg"
              >
                <FileText className="w-5 h-5 mr-2" />
                {t('landing.ctaAIAnalysis')}
              </Link>
              {/* <Link to="/templates" className="btn-outline text-lg px-8 py-4">
                Browse Templates
              </Link> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{t(stat.labelKey)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-title">
              {t('landing.featuresTitle')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card text-center hover:shadow-soft transition-shadow duration-300"
              >
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Preview */}
      {/* <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Pick one of many world-class templates and build your resume in minutes
            </h2>
            <p className="section-subtitle">
              Professional templates designed for every industry and career level
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[1, 2, 3, 4, 5, 6].map((template) => (
              <motion.div
                key={template}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: template * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="bg-gray-200 rounded-lg h-64 mb-4 group-hover:shadow-lg transition-shadow duration-300"></div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Template {template}</h3>
                  <Link to="/templates" className="text-primary-600 hover:text-primary-700 font-medium">
                    Use This Template
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/templates" className="btn-primary">
              See All Resume Templates
            </Link>
          </div>
        </div>
      </section> */}

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-title">
              {t('landing.testimonialsTitle')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="card"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role} at {testimonial.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-white mb-6"
          >
            {t('landing.ctaBottomTitle')}
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link to="/register" className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg text-lg transition-colors duration-200">
              {t('landing.ctaBottomButton')}
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
